import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '@clerk/clerk-react'
import {
  api,
  getSessionCookieToken,
  type ByokConfigResponse,
  type ByokKeyStatus,
} from '../api/client'

const PROVIDER_DISPLAY: Record<string, string> = {
  anthropic: 'Anthropic (Claude)',
  openai: 'OpenAI',
  groq: 'Groq',
  openrouter: 'OpenRouter (Multi-Provider)',
}

function statusLabel(status: ByokKeyStatus | null): { text: string; cls: string } {
  switch (status) {
    case 'validated':
      return { text: 'Validated', cls: 'health-badge health-ok' }
    case 'pending':
      return { text: 'Pending validation', cls: 'health-badge health-missing' }
    case 'invalid':
      return { text: 'Invalid key', cls: 'health-badge health-error' }
    case 'revoked':
      return { text: 'Revoked', cls: 'health-badge health-error' }
    default:
      return { text: 'Not configured', cls: 'health-badge health-missing' }
  }
}

type Props = {
  isAdmin: boolean
  onFeatureEnabled?: (enabled: boolean) => void
}

export function ByokSettings({ isAdmin, onFeatureEnabled }: Props) {
  const { getToken } = useAuth()

  const [config, setConfig] = useState<ByokConfigResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)

  // Save form state
  const [provider, setProvider] = useState('anthropic')
  const [apiKey, setApiKey] = useState('')
  const [baseUrl, setBaseUrl] = useState('')
  const [rcaModel, setRcaModel] = useState('')
  const [codeFixModel, setCodeFixModel] = useState('')
  const [fallbackEnabled, setFallbackEnabled] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveMsg, setSaveMsg] = useState<string | null>(null)

  // Validation state
  const [validating, setValidating] = useState(false)
  const [validateMsg, setValidateMsg] = useState<{ ok: boolean; text: string } | null>(null)

  // Toggle state
  const [togglingRca, setTogglingRca] = useState(false)
  const [togglingCodeFix, setTogglingCodeFix] = useState(false)

  // Revoke state
  const [revoking, setRevoking] = useState(false)

  const getToken_ = useCallback(async () => {
    return getSessionCookieToken() ?? (await getToken())
  }, [getToken])

  const loadConfig = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const token = await getToken_()
      if (!token) return
      const cfg = await api.getByokConfig(token)
      setConfig(cfg)
      onFeatureEnabled?.(cfg.feature_enabled)
      if (cfg.provider) setProvider(cfg.provider)
      if (cfg.rca_model) setRcaModel(cfg.rca_model)
      if (cfg.code_fix_model) setCodeFixModel(cfg.code_fix_model)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }, [getToken_, onFeatureEnabled])

  useEffect(() => {
    void loadConfig()
  }, [loadConfig])

  const availableModels = config?.provider_models?.[provider] ?? []

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    if (!apiKey.trim()) {
      setSaveMsg('Enter an API key.')
      return
    }
    setSaving(true)
    setSaveMsg(null)
    try {
      const token = await getToken_()
      if (!token) throw new Error('Not authenticated')
      const updated = await api.saveByokConfig(token, {
        provider,
        api_key: apiKey,
        base_url: baseUrl || null,
        rca_model: rcaModel || null,
        code_fix_model: codeFixModel || null,
        fallback_enabled: fallbackEnabled,
      })
      setConfig(updated)
      setApiKey('')
      setValidateMsg(null)
      setSaveMsg('Key saved. Click "Test connection" to verify it works.')
    } catch (err) {
      setSaveMsg((err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  async function handleValidate() {
    setValidating(true)
    setValidateMsg(null)
    try {
      const token = await getToken_()
      if (!token) throw new Error('Not authenticated')
      const result = await api.validateByokKey(token)
      setValidateMsg({ ok: result.success, text: result.message })
      await loadConfig()
    } catch (err) {
      setValidateMsg({ ok: false, text: (err as Error).message })
    } finally {
      setValidating(false)
    }
  }

  async function handleToggle(field: 'rca_enabled' | 'code_fix_enabled', current: boolean) {
    const setter = field === 'rca_enabled' ? setTogglingRca : setTogglingCodeFix
    setter(true)
    try {
      const token = await getToken_()
      if (!token) throw new Error('Not authenticated')
      const updated = await api.updateByokToggles(token, { [field]: !current })
      setConfig(updated)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setter(false)
    }
  }

  async function handleRevoke() {
    if (!window.confirm('Remove this API key? SelfHeal will stop using it immediately.')) {
      return
    }
    setRevoking(true)
    try {
      const token = await getToken_()
      if (!token) throw new Error('Not authenticated')
      await api.revokeByokConfig(token)
      await loadConfig()
      setValidateMsg(null)
      setSaveMsg(null)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setRevoking(false)
    }
  }

  const status = statusLabel(config?.key_status ?? null)
  const isValidated = config?.key_status === 'validated'

  // Feature flag off for this org — hide the panel entirely.
  if (!loading && config !== null && !config.feature_enabled) {
    return null
  }

  return (
    <div className={`cred-card ${expanded ? 'cred-card--open' : ''}`} style={{ marginBottom: '8px' }}>
      <button
        type="button"
        className="cred-card-header"
        onClick={() => setExpanded((v) => !v)}
        style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
      >
        <div className="byok-header-icon-container">
          <svg
            className="byok-header-svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="7.5" cy="15.5" r="5.5" fill="rgba(52, 211, 153, 0.08)" />
            <path d="m21 2-9.6 9.6" />
            <path d="m15.5 7.5 3 3L22 7l-3-3" />
          </svg>
        </div>
        <span className="cred-card-title" style={{ fontWeight: 600 }}>
          AI Provider Keys (BYOK)
        </span>
        <span
          className="conn-type-badge"
          style={{
            background: 'rgba(52, 211, 153, 0.05)',
            color: 'var(--accent-light)',
            border: '1px solid rgba(52, 211, 153, 0.15)',
          }}
        >
          Bring Your Own Key
        </span>
        {config?.configured && <span className={status.cls}>{status.text}</span>}
        <span className="cred-card-chevron">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="cred-card-body">
          {loading && <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading…</p>}
          {error && <p style={{ color: 'var(--error)', fontSize: '13px' }}>{error}</p>}

          {/* Current config summary */}
          {config?.configured && !loading && (
            <div
              style={{
                background: 'rgba(0,0,0,0.04)',
                borderRadius: '6px',
                padding: '10px 14px',
                marginBottom: '14px',
                fontSize: '13px',
              }}
            >
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                <span>
                  <strong>Provider:</strong> {PROVIDER_DISPLAY[config.provider ?? ''] ?? config.provider}
                </span>
                <span>
                  <strong>Key:</strong>{' '}
                  <code style={{ fontFamily: 'monospace' }}>{config.key_fingerprint ?? '—'}</code>
                </span>
                <span className={status.cls}>{status.text}</span>
                {config.updated_at && (
                  <span style={{ color: 'var(--text-secondary)' }}>
                    Updated {new Date(config.updated_at).toLocaleDateString()}
                  </span>
                )}
              </div>

              {/* Workflow toggles */}
              {isAdmin && (
                <div style={{ display: 'flex', gap: '20px', marginTop: '10px', flexWrap: 'wrap' }}>
                  <label
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: isValidated ? 'pointer' : 'not-allowed' }}
                    title={!isValidated ? 'Run Test connection first' : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={config.rca_enabled}
                      disabled={!isValidated || togglingRca}
                      onChange={() => handleToggle('rca_enabled', config.rca_enabled)}
                    />
                    BYOK for RCA
                  </label>
                  <label
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: isValidated ? 'pointer' : 'not-allowed' }}
                    title={!isValidated ? 'Run Test connection first' : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={config.code_fix_enabled}
                      disabled={!isValidated || togglingCodeFix}
                      onChange={() => handleToggle('code_fix_enabled', config.code_fix_enabled)}
                    />
                    BYOK for Code Fix
                  </label>
                </div>
              )}
            </div>
          )}

          {/* Non-admin read-only view */}
          {!isAdmin && (
            <p style={{ color: 'var(--text-secondary)', fontSize: '13px', margin: '0 0 12px' }}>
              Only workspace admins can manage API keys.
            </p>
          )}

          {/* Admin: save form */}
          {isAdmin && (
            <>
              <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '1', minWidth: '160px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Provider</span>
                    <select
                      value={provider}
                      onChange={(e) => {
                        setProvider(e.target.value)
                        setRcaModel('')
                        setCodeFixModel('')
                      }}
                      style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '13px' }}
                    >
                      {(config?.supported_providers ?? ['anthropic', 'openai', 'groq', 'openrouter']).map((p) => (
                        <option key={p} value={p}>
                          {PROVIDER_DISPLAY[p] ?? p}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '2', minWidth: '240px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                      API Key <span style={{ color: 'var(--error)' }}>*</span>
                    </span>
                    <input
                      type="password"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder={config?.configured ? 'Enter new key to rotate' : 'sk-...'}
                      autoComplete="new-password"
                      style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '13px' }}
                    />
                    {provider === 'anthropic' && (
                      <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                        An API key (sk-ant-api…) covers RCA and Code Fix. A <code>claude setup-token</code> OAuth
                        token (sk-ant-oat…) authenticates the Claude Code CLI only, so it covers Code Fix.
                      </span>
                    )}
                  </label>
                </div>

                <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Base URL (optional)</span>
                  <input
                    type="url"
                    value={baseUrl}
                    onChange={(e) => setBaseUrl(e.target.value)}
                    placeholder="https://api.provider.com (leave blank for provider default)"
                    style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '13px' }}
                  />
                </label>

                {availableModels.length > 0 && (
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '1', minWidth: '200px' }}>
                      <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Default model for RCA</span>
                      <select
                        value={rcaModel}
                        onChange={(e) => setRcaModel(e.target.value)}
                        style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '13px' }}
                      >
                        <option value="">Provider default</option>
                        {availableModels.map((m) => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '1', minWidth: '200px' }}>
                      <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Default model for Code Fix</span>
                      <select
                        value={codeFixModel}
                        onChange={(e) => setCodeFixModel(e.target.value)}
                        style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '13px' }}
                      >
                        <option value="">Provider default</option>
                        {availableModels.map((m) => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                )}

                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={fallbackEnabled}
                    onChange={(e) => setFallbackEnabled(e.target.checked)}
                  />
                  Fall back to SelfHeal key if mine fails
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>(disabling fallback is recommended for compliance)</span>
                </label>

                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    type="submit"
                    className="verify-btn"
                    disabled={saving}
                    style={{ background: 'var(--accent)', color: '#fff', border: 'none' }}
                  >
                    {saving ? 'Saving…' : config?.configured ? 'Rotate key' : 'Save key'}
                  </button>

                  {config?.configured && config.key_status !== 'revoked' && (
                    <button
                      type="button"
                      className="verify-btn"
                      onClick={handleValidate}
                      disabled={validating}
                    >
                      {validating ? 'Testing…' : 'Test connection'}
                    </button>
                  )}

                  {config?.configured && (
                    <button
                      type="button"
                      className="verify-btn"
                      onClick={handleRevoke}
                      disabled={revoking}
                      style={{ color: 'var(--error)', border: '1px solid var(--error)' }}
                    >
                      {revoking ? 'Revoking…' : 'Revoke'}
                    </button>
                  )}
                </div>

                {saveMsg && (
                  <p style={{ fontSize: '13px', color: saveMsg.includes('saved') ? 'var(--success)' : 'var(--error)', margin: 0 }}>
                    {saveMsg}
                  </p>
                )}
                {validateMsg && (
                  <p style={{ fontSize: '13px', color: validateMsg.ok ? 'var(--success)' : 'var(--error)', margin: 0 }}>
                    {validateMsg.text}
                  </p>
                )}
              </form>
            </>
          )}
        </div>
      )}
    </div>
  )
}
